import type { Fighter, Lunge, Scene } from './types';
import { ENTER_DUR, KNIFE_DEATH_FRAC, STANCE_STEP, nearestEnemyId } from './battleConfig';
import { aimTargetOf, type BattleEngineOpts, type PlaybackState } from './playbackState';
import { playSfx } from '../game/sfx';

const STEP_EPS = 3;
const STEP_GAP = 0.36;
const STEP_VOL = 0.22;

const advanceLunge = (sc: Scene, f: Fighter, lg: Lunge, n: number): void => {
  const tgt = sc.byId[lg.targetId];
  const destX = lg.homeX + ((tgt ? tgt.x : lg.homeX) - lg.homeX) * lg.frac;
  const destY = lg.homeY + ((tgt ? tgt.y : lg.homeY) - lg.homeY) * lg.frac;
  if (f.dead) {
    if (!lg.struck) { lg.struck = true; lg.strike(); }
    f.x = lg.homeX + (destX - lg.homeX) * (KNIFE_DEATH_FRAC / lg.frac) - f.dir * 12;
    f.y = lg.homeY + (destY - lg.homeY) * (KNIFE_DEATH_FRAC / lg.frac) + 28;
    f.lunge = undefined;
    return;
  }
  const p = (n - lg.t0) / lg.dur;
  if (p >= 1) { f.x = lg.homeX; f.y = lg.homeY; f.lunge = undefined; return; }
  const k = Math.sin(Math.PI * Math.max(0, p));
  f.x = lg.homeX + (destX - lg.homeX) * k;
  f.y = lg.homeY + (destY - lg.homeY) * k;
  if (!lg.struck && p >= 0.5) { lg.struck = true; lg.strike(); }
};

const turnToward = (f: Fighter, aimAt: number): void => {
  let d = aimAt - f.aim;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  f.aim += d * 0.3;
};

export const advanceFighters = (
  sc: Scene, opts: BattleEngineOpts, n: number, replaying: boolean, state: PlaybackState,
): void => {
  const youId = opts.getYouId();
  for (const f of sc.fighters) {
    f.recoil = Math.max(0, f.recoil - 0.12);
    if (f.lunge) { advanceLunge(sc, f, f.lunge, n); continue; }
    if (f.dead) continue;
    f.targetX = f.spawnX + f.dir * (STANCE_STEP[f.combatStance] ?? 0);
    const enterEnd = state.enterAt + ENTER_DUR;
    if (state.enterAt && n < enterEnd && f.enterFromX != null) {
      const p = Math.max(0, Math.min(1, (n - state.enterAt) / ENTER_DUR));
      const e = p * p * (3 - 2 * p);
      f.x = f.enterFromX + (f.targetX - f.enterFromX) * e;
    } else {
      f.x += (f.targetX - f.x) * 0.1;
    }
    const entering = !!state.enterAt && n < enterEnd && f.enterFromX != null;
    if (entering || Math.abs(f.targetX - f.x) > STEP_EPS) {
      if (f.stepAt == null) f.stepAt = n + ((((f.spawnX % 100) + 100) % 100) / 100) * STEP_GAP;
      else if (n >= f.stepAt) { playSfx('footstep', STEP_VOL); f.stepAt = n + STEP_GAP; }
    } else {
      f.stepAt = undefined;
    }
    const chosen = aimTargetOf(f.id, youId, replaying, state, opts);
    const friendly = !!chosen && sc.byId[chosen]?.team === f.team;
    let aimId: string | null = null;
    if (f.engagedId && !sc.byId[f.engagedId]?.dead) aimId = f.engagedId;
    else if (chosen && !friendly && !sc.byId[chosen]?.dead) aimId = chosen;
    else if (!friendly) aimId = nearestEnemyId(sc, f);
    const tg = aimId ? sc.byId[aimId] : null;
    const aimAt = tg && !tg.dead
      ? Math.atan2(tg.y - f.y, tg.x - f.x)
      : (friendly ? (f.dir > 0 ? 0 : Math.PI) : null);
    if (aimAt != null) turnToward(f, aimAt);
  }
};
